import React, { createContext, useContext, useMemo } from 'react';
import { DEFAULT_GUIDES } from './GuideControlsPanel.jsx';

/* GuidesContext — app-wide switch board for the six guide layers.

   Wrap the app once at the top of <App/>:
     <GuidesProvider value={scenario?.guides}>
       {...}
     </GuidesProvider>

   Then any component can read whether a specific guide is enabled:
     const { tooltips, eduTips, drillDowns, subtitles, modifiedBadges,
             sparklineTrends } = useGuides();

   Any missing key defaults to TRUE (via DEFAULT_GUIDES spread) so new
   code that reads a flag before the scenario object catches up still
   shows the guide. The panel at Settings → Guide Controls mutates the
   scenario.guides object; this provider re-renders with the merged view.

   Consumers should short-circuit when a flag is false — e.g.:
     if (!useGuides().eduTips) return null;
*/
const GuidesContext = createContext(DEFAULT_GUIDES);

export function GuidesProvider({ value, children }) {
  /* Memoize the merged object so context consumers only re-render when the
     guide state actually changes (not every render of the provider's parent). */
  const merged = useMemo(
    () => ({ ...DEFAULT_GUIDES, ...(value ?? {}) }),
    [value],
  );
  return (
    <GuidesContext.Provider value={merged}>
      {children}
    </GuidesContext.Provider>
  );
}

export function useGuides() {
  return useContext(GuidesContext);
}
